import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {window} from 'ssr-window';
import BrandUtils from '@spothero/utils/brand';
import DOMUtils from '@spothero/utils/dom';
import Card from 'common/card/Card';
import Link from 'common/router/Link';
import AirportFacilityDetails from 'common/airport/AirportFacilityDetails';
import ImportantInformation from 'common/airport/ImportantInformationResponsive';
import PriceDisplay from 'common/airport/PriceDisplay';

export default class SearchAirportCardResponsive extends Component {
    static propTypes = {
        spot: PropTypes.object.isRequired,
        starts: PropTypes.string,
        ends: PropTypes.string,
        updateSelectedSpot: PropTypes.func.isRequired,
        pushTo: PropTypes.func.isRequired
    };
    static defaultProps = {
        priceIsDefault: true
    };

    constructor(props) {
        super(props);

        const {
            spot: {
                facility: {
                    spot_details_url: spotDetailsUrl = '',
                    airport: {
                        lowest_daily_rate: lowestDailyRate
                    } = {}
                } = {},
                hourly_rates: hourlyRates
            } = {},
            starts,
            ends
        } = props;

        this._url = `/${spotDetailsUrl}/?airport=true&starts=${starts}&ends=${ends}`;
        this._price = lowestDailyRate;
        this._priceIsDefault = true;

        if (hourlyRates && hourlyRates.length) {
            this._price = hourlyRates[0].consumer_breakdown && hourlyRates[0].consumer_breakdown.price_per_day;
            this._priceIsDefault = false;
        }
    }

    _onMouseEnterSpot = () => {
        const {
            spot: {
                spotId,
                latitude
            },
        } = this.props;

        this._dispatchSpotHoverEvent({
            spotId,
            latitude,
            hovered: true,
            activeMarker: true
        });
    }

    _onMouseLeaveSpot = () => {
        const {
            spot: {
                spotId,
                latitude
            },
        } = this.props;

        this._dispatchSpotHoverEvent({
            spotId,
            latitude,
            hovered: false,
            activeMarker: false
        });
    }

    _onCardClick = evt => {
        if (window.innerWidth > BrandUtils.getBreakpoint('lg')
            || DOMUtils.hasClass(evt.target, 'AirportFacilityDetails-admin-link')
        ) {
            return false;
        } else {
            this._onResultClick(evt);
        }
    }

    _onResultClick = evt => {
        evt.preventDefault();
        const {
            spot,
            updateSelectedSpot,
            pushTo
        } = this.props;

        updateSelectedSpot({
            spot,
            referrer: 'list'
        });

        pushTo(this._url);

        // Returning false from an anchor tag prevents the default behavior of following the href
        // In this instance, the href and this._url are for SEO purposes only
        return false;
    }

    _dispatchSpotHoverEvent = data => {
        let customEvent = null;

        if (typeof window.CustomEvent === 'function') {
            customEvent = new CustomEvent('spotHover', {detail: data});
        } else {
            customEvent = document.createEvent('CustomEvent');
            customEvent.initCustomEvent('spotHover', true, true, data);
        }

        document.dispatchEvent(customEvent);
    }

    render() {
        const {
            starts,
            ends,
            spot: {
                facility,
                selectedRate: {
                    unavailable: isUnavailable,
                    unavailable_reason: unavailableReason
                }
            },
        } = this.props;
        const bookButtonClasses = classNames(
            'Button',
            {'Button-primary': !isUnavailable},
            {'Button-secondary': isUnavailable}
        );
        const unavailableTag = Boolean(unavailableReason) && (
            <div className="SearchAirportCardResponsive-unavailable"><sup>*</sup> {unavailableReason}</div>
        );

        return (
            <section
                className="SearchAirportCardResponsive"
                itemProp="item"
                itemScope
                itemType="http://schema.org/Product"
                onMouseEnter={this._onMouseEnterSpot}
                onMouseLeave={this._onMouseLeaveSpot}
                onClick={this._onCardClick}
            >
                <Card heading={facility.tag}>
                    <link
                        itemProp="url"
                        href={this._url}
                    />
                    <div className="SearchAirportCardResponsive-facility">
                        <div className="SearchAirportCardResponsive-location">
                            <div className="SearchAirportCardResponsive-description">
                                <AirportFacilityDetails
                                    facility={facility}
                                    onFacilityClick={this._onResultClick}
                                />
                                {unavailableTag}
                            </div>
                            <PriceDisplay
                                isDefault={this._priceIsDefault}
                                price={this._price}
                            />
                            <div className="SearchAirportCardResponsive-lg-book-now">
                                <Link
                                    className={bookButtonClasses}
                                    to={this._url}
                                    itemProp="url"
                                    onClick={this._onResultClick}
                                >
                                    Book Now
                                </Link>
                                {unavailableTag}
                            </div>
                        </div>
                        <div className="SearchAirportCardResponsive-details">
                            <ImportantInformation
                                data={facility.airport}
                            />
                        </div>
                    </div>
                    <div className="SearchAirportCardResponsive-offer">
                        {Boolean(facility.tag) &&
                            <span className="SearchAirportCardResponsive-featured-tag">{facility.tag}</span>
                        }
                        <div
                            className="SearchAirportCardResponsive-offer-content"
                            itemProp="offers"
                            itemScope
                            itemType="http://schema.org/Offer"
                        >
                            <meta
                                itemProp="name"
                                content="Parking"
                            />
                            <meta
                                itemProp="description"
                                content={`Reserved Parking at ${facility.title}`}
                            />
                            <span
                                itemProp="seller"
                                itemScope
                                itemType="http://schema.org/Organization"
                            >
                                <meta
                                    itemProp="name"
                                    content="SpotHero"
                                />
                                <meta
                                    itemProp="url"
                                    content="https://spothero.com"
                                />
                            </span>
                            {(starts && ends) &&
                                <Fragment>
                                    <meta
                                        itemProp="validFrom"
                                        content={starts}
                                    />
                                    <meta
                                        itemProp="validThrough"
                                        content={ends}
                                    />
                                </Fragment>
                            }
                            <meta
                                itemProp="availability"
                                content="http://schema.org/InStock"
                            />
                            <PriceDisplay
                                isDefault={this._priceIsDefault}
                                price={this._price}
                            />
                            <div className="SearchAirportCardResponsive-book-now">
                                <Link
                                    className={bookButtonClasses}
                                    to={this._url}
                                    itemProp="url"
                                    onClick={this._onResultClick}
                                >
                                    Book Now
                                </Link>
                                {unavailableTag}
                            </div>
                        </div>
                    </div>
                </Card>
            </section>
        );
    }
}
